import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { X, Bookmark, MapPin, FileText, Sparkles, Send, Briefcase, Mail, Download } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { exportCandidateHtml } from "@/lib/exportHtml";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const REC_STYLE = {
  strong_fit: "text-teal-300 border-teal-500/40 bg-teal-500/10",
  possible_fit: "text-amber-300 border-amber-500/40 bg-amber-500/10",
  weak_fit: "text-red-300 border-red-500/40 bg-red-500/10",
};

function Bar({ label, value }) {
  return (
    <div>
      <div className="flex justify-between text-xs text-slate-400 mb-1"><span>{label}</span><span className="font-mono text-teal-300">{value}%</span></div>
      <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden"><div className="h-full bg-gradient-to-r from-teal-400 to-cyan-500" style={{ width: `${value}%` }} /></div>
    </div>
  );
}

export default function CandidateDrawer({ candidate, isAgency, jobs = [], clients = [], onClose, onToggleSave }) {
  const [jobId, setJobId] = useState("");
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [clientId, setClientId] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [messages, setMessages] = useState([]);

  useEffect(() => {
    api.get(`/candidates/${candidate.id}/messages`).then((r) => setMessages(r.data)).catch(() => {});
  }, [candidate.id]);

  const sendMessage = async () => {
    if (!subject.trim() || !body.trim()) { toast.error("Add a subject and message"); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/candidates/${candidate.id}/message`, { subject, body });
      setMessages((prev) => [data, ...prev]);
      setSubject(""); setBody("");
      toast.success("Message sent to candidate");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const viewResume = () => {
    const token = localStorage.getItem("wo_token");
    const path = candidate.resume_url.replace(/^\/api\/files\//, "");
    window.open(`${BACKEND_URL}/api/files/${path}?auth=${token}`, "_blank");
  };

  const analyze = async () => {
    if (!jobId) { toast.error("Pick a job to match against first"); return; }
    setAnalyzing(true); setAnalysis(null);
    try {
      const { data } = await api.post("/match/explain", { candidate_id: candidate.id, job_id: jobId });
      setAnalysis(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAnalyzing(false); }
  };

  const submitToClient = async () => {
    if (!clientId) { toast.error("Choose a client"); return; }
    setSubmitting(true);
    try {
      await api.post("/agency/submissions", { candidate_id: candidate.id, client_id: clientId, job_id: jobId || null, note });
      toast.success("Candidate submitted to client");
      setNote(""); setClientId("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-[60]">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute right-0 top-0 h-full w-full max-w-md liquid-glass border-l border-white/10 overflow-y-auto p-6 sm:p-8" data-testid="candidate-detail-drawer">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-teal-400 to-blue-600 flex items-center justify-center text-xl font-bold text-slate-900 shrink-0">
              {candidate.name?.[0]?.toUpperCase() || "C"}
            </div>
            <div className="min-w-0">
              <h2 className="font-display font-bold text-lg text-slate-100 truncate">{candidate.name}</h2>
              <p className="text-slate-400 text-sm truncate">{candidate.headline}</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => onToggleSave(candidate)} data-testid="drawer-save-candidate"
              className={`p-2 rounded-lg ${candidate.saved ? "text-teal-300" : "text-slate-400 hover:text-teal-300"}`}>
              <Bookmark size={18} fill={candidate.saved ? "currentColor" : "none"} />
            </button>
            <button onClick={onClose} data-testid="drawer-close" className="p-2 text-slate-400 hover:text-white"><X size={20} /></button>
          </div>
        </div>

        {candidate.location && <div className="mt-4 flex items-center gap-1.5 text-sm text-slate-300"><MapPin size={14} className="text-teal-400" /> {candidate.location}</div>}
        {candidate.bio && <p className="mt-3 text-sm text-slate-400 leading-relaxed">{candidate.bio}</p>}

        {candidate.skills?.length > 0 && (
          <div className="mt-5">
            <h4 className="font-display font-semibold text-slate-200 mb-2 text-sm">Skills</h4>
            <div className="flex flex-wrap gap-2">
              {candidate.skills.map((s) => <span key={s} className="text-xs px-2.5 py-1 rounded-full bg-slate-800/80 text-cyan-300 border border-cyan-500/20">{s}</span>)}
            </div>
          </div>
        )}

        {candidate.experiences?.length > 0 && (
          <div className="mt-5">
            <h4 className="font-display font-semibold text-slate-200 mb-2 text-sm">Experience</h4>
            <div className="space-y-2">
              {candidate.experiences.map((e, i) => (
                <div key={i} className="rounded-lg bg-slate-950/40 border border-white/10 p-3">
                  <div className="text-sm text-slate-200 font-medium">{e.title} {e.company && <span className="text-slate-500">· {e.company}</span>}</div>
                  {(e.start || e.end) && <div className="text-xs text-slate-500">{e.start} – {e.end || "Present"}</div>}
                  {e.description && <div className="text-xs text-slate-400 mt-1">{e.description}</div>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* AI match analysis */}
        <div className="mt-6 rounded-xl bg-slate-950/40 border border-teal-500/15 p-4">
          <h4 className="font-display font-semibold text-slate-200 mb-2 text-sm flex items-center gap-2"><Sparkles size={15} className="text-teal-400" /> Match analysis</h4>
          <Select value={jobId} onValueChange={setJobId}>
            <SelectTrigger data-testid="drawer-match-job-select" className="bg-slate-950/50 border-white/10 text-slate-200 text-sm"><SelectValue placeholder="Match against one of your jobs…" /></SelectTrigger>
            <SelectContent className="text-slate-200 max-h-64">
              {jobs.length === 0 && <div className="px-3 py-2 text-xs text-slate-500">Post a job first to run matching</div>}
              {jobs.map((j) => <SelectItem key={j.id} value={j.id}>{j.title}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={analyze} disabled={analyzing || !jobId} data-testid="drawer-analyze-btn"
            className="w-full mt-3 bg-gradient-to-r from-teal-400 to-cyan-500 text-slate-900 font-semibold hover:opacity-90 disabled:opacity-40">
            {analyzing ? "Analyzing…" : "Analyze match with AI"}
          </Button>

          {analysis && (
            <div className="mt-4 space-y-3" data-testid="match-analysis-result">
              <div className="flex items-center justify-between">
                <span className="font-mono text-2xl font-bold text-teal-300">{analysis.score}%</span>
                <span className={`text-xs px-2.5 py-1 rounded-full border capitalize ${REC_STYLE[analysis.recommendation] || "text-slate-300 border-white/10"}`}>
                  {(analysis.recommendation || "").replace("_", " ")}
                </span>
              </div>
              <div className="space-y-2">
                <Bar label="Skills" value={analysis.breakdown?.skills ?? 0} />
                <Bar label="Location" value={analysis.breakdown?.location ?? 0} />
                <Bar label="Experience" value={analysis.breakdown?.experience ?? 0} />
              </div>
              <p className="text-sm text-slate-300">{analysis.summary}</p>
              {analysis.strengths?.length > 0 && (
                <div><div className="text-xs font-semibold text-teal-300 mb-1">Strengths</div>
                  <ul className="list-disc pl-4 text-xs text-slate-400 space-y-0.5">{analysis.strengths.map((x, i) => <li key={i}>{x}</li>)}</ul></div>
              )}
              {analysis.gaps?.length > 0 && (
                <div><div className="text-xs font-semibold text-amber-300 mb-1">Gaps</div>
                  <ul className="list-disc pl-4 text-xs text-slate-400 space-y-0.5">{analysis.gaps.map((x, i) => <li key={i}>{x}</li>)}</ul></div>
              )}
            </div>
          )}
        </div>

        {/* Agency: submit to client */}
        {isAgency && (
          <div className="mt-5 rounded-xl bg-slate-950/40 border border-cyan-500/15 p-4">
            <h4 className="font-display font-semibold text-slate-200 mb-2 text-sm flex items-center gap-2"><Briefcase size={15} className="text-cyan-400" /> Present to client</h4>
            <Select value={clientId} onValueChange={setClientId}>
              <SelectTrigger data-testid="drawer-client-select" className="bg-slate-950/50 border-white/10 text-slate-200 text-sm"><SelectValue placeholder="Select client company…" /></SelectTrigger>
              <SelectContent className="text-slate-200 max-h-64">
                {clients.length === 0 && <div className="px-3 py-2 text-xs text-slate-500">Add a client company first</div>}
                {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="drawer-submit-note"
              placeholder="Note to the client (optional)" className="mt-2 bg-slate-950/50 border-white/10 text-slate-100 text-sm min-h-[70px]" />
            <Button onClick={submitToClient} disabled={submitting || !clientId} data-testid="drawer-submit-client-btn"
              className="w-full mt-2 bg-gradient-to-r from-cyan-400 to-blue-500 text-slate-900 font-semibold hover:opacity-90 disabled:opacity-40">
              <Send size={15} className="mr-2" /> {submitting ? "Submitting…" : "Submit candidate"}
            </Button>
          </div>
        )}

        {/* One-click outreach */}
        <div className="mt-5 rounded-xl bg-slate-950/40 border border-white/10 p-4">
          <h4 className="font-display font-semibold text-slate-200 mb-2 text-sm flex items-center gap-2"><Mail size={15} className="text-teal-400" /> Contact candidate</h4>
          <input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="outreach-subject"
            placeholder="Subject (e.g. A role that fits you)" className="w-full rounded-lg bg-slate-950/50 border border-white/10 text-slate-100 text-sm px-3 py-2 outline-none focus:border-teal-500/40" />
          <Textarea value={body} onChange={(e) => setBody(e.target.value)} data-testid="outreach-body"
            placeholder="Write a short message…" className="mt-2 bg-slate-950/50 border-white/10 text-slate-100 text-sm min-h-[70px]" />
          <Button onClick={sendMessage} disabled={sending} data-testid="outreach-send-btn"
            className="w-full mt-2 bg-gradient-to-r from-teal-400 to-cyan-500 text-slate-900 font-semibold hover:opacity-90 disabled:opacity-40">
            <Send size={15} className="mr-2" /> {sending ? "Sending…" : "Send message"}
          </Button>
          {messages.length > 0 && (
            <div className="mt-3 space-y-2" data-testid="outreach-history">
              <div className="text-xs text-slate-500">Sent {messages.length} message{messages.length > 1 ? "s" : ""}</div>
              {messages.slice(0, 3).map((m) => (
                <div key={m.id} className="rounded-lg bg-slate-900/50 border border-white/5 p-2.5">
                  <div className="text-xs text-slate-200 font-medium">{m.subject}</div>
                  <div className="text-xs text-slate-500 truncate">{m.body}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-6 space-y-2">
          <Button onClick={() => exportCandidateHtml(candidate)} data-testid="drawer-download-profile"
            className="w-full border border-white/10 bg-white/5 text-slate-100 hover:bg-white/10">
            <Download size={16} className="mr-2" /> Download profile (HTML / PDF)
          </Button>
          {candidate.resume_url ? (
            <Button onClick={viewResume} data-testid="drawer-view-resume"
              className="w-full border border-white/10 bg-white/5 text-slate-100 hover:bg-white/10">
              <FileText size={16} className="mr-2" /> View résumé
            </Button>
          ) : (
            <div className="text-center text-sm text-slate-500 py-3 border border-white/10 rounded-lg">No résumé attached</div>
          )}
        </div>
      </div>
    </div>
  );
}
